import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
  read: boolean;
  targetUserId?: string; // Optional: if set, only this user should see the notification
  targetRole?: string; // Optional: if set, only users with this role should see the notification
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'timestamp' | 'read'> & { id?: string; targetUserId?: string; targetRole?: string }) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  markAllAsReadForUser: (userId: string, userRole?: string) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  getNotificationsForUser: (userId: string, userRole?: string) => Notification[];
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((notification: Omit<Notification, 'timestamp' | 'read'> & { id?: string; targetUserId?: string; targetRole?: string }) => {
    const newNotification: Notification = {
      title: notification.title,
      message: notification.message,
      type: notification.type,
      action: notification.action,
      id: notification.id || `${Date.now()}-${Math.random().toString(36).substring(7)}`,
      timestamp: new Date(),
      read: false,
      targetUserId: notification.targetUserId,
      targetRole: notification.targetRole,
    };
    setNotifications((prev) => {
      // Check if notification with this ID already exists
      const existingIndex = prev.findIndex(n => n.id === newNotification.id);
      if (existingIndex !== -1) {
        // Update existing notification instead of adding duplicate
        return prev.map((n, i) => i === existingIndex ? newNotification : n);
      }
      // Add new notification
      return [newNotification, ...prev];
    });
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, read: true }))
    );
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  }, []);

  const markAllAsReadForUser = useCallback((userId: string, userRole?: string) => {
    setNotifications((prev) =>
      prev.map((notification) => {
        // Only mark as read if this notification belongs to/is visible to the user
        const isTargetedToUser = !notification.targetUserId || notification.targetUserId === userId;
        const isTargetedToRole = !notification.targetRole || notification.targetRole === userRole;
        if (isTargetedToUser && isTargetedToRole) {
          return { ...notification, read: true };
        }
        return notification;
      })
    );
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const getNotificationsForUser = useCallback((userId: string, userRole?: string) => {
    return notifications.filter(n => {
      const isTargetedToUser = !n.targetUserId || n.targetUserId === userId;
      const isTargetedToRole = !n.targetRole || n.targetRole === userRole;
      return isTargetedToUser && isTargetedToRole;
    });
  }, [notifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        markAsRead,
        markAllAsRead,
        markAllAsReadForUser,
        removeNotification,
        clearAll,
        getNotificationsForUser,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}